import { customTheme } from '@/config/theme'
import { cartoThemeOptions } from '@carto/meridian-ds/theme'
import { createTheme, type Theme } from '@mui/material'
import type { Palette, PaletteColor } from '@mui/material/styles'

export interface CustomTheme extends Theme {
  palette: CustomPalette
}

interface CustomPalette extends Palette {
  appBar: PaletteColor
}

// Create base theme with CARTO options
const baseTheme = createTheme(cartoThemeOptions)

// Custom theme overrides for step components (migrated from Material UI v4 to v5)
const stepOverrides = {
  components: {
    MuiStepLabel: {
      styleOverrides: {
        label: {
          ...baseTheme.typography.subtitle1,
          color: baseTheme.palette.text.primary,
          fontWeight: 600,
          '&.Mui-completed, &.Mui-active': {
            color: baseTheme.palette.text.primary,
            fontWeight: 600,
          },
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          color: baseTheme.palette.text.primary,
          '&.Mui-completed': {
            color: baseTheme.palette.text.primary,
          },
          '&.Mui-active': {
            color: baseTheme.palette.text.primary,
          },
        },
        text: {
          fontFamily: baseTheme.typography.fontFamily,
          fontWeight: baseTheme.typography.subtitle1?.fontWeight,
        },
      },
    },
    MuiStepConnector: {
      styleOverrides: {
        lineVertical: {
          borderLeftWidth: 2,
          borderColor: baseTheme.palette.divider,
        },
      },
    },
    MuiStepContent: {
      styleOverrides: {
        root: {
          borderLeftWidth: 2,
          borderColor: baseTheme.palette.divider,
        },
      },
    },
    // Force Montserrat for headings and buttons
    MuiTypography: {
      styleOverrides: {
        h1: {
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 600,
          WebkitFontSmoothing: 'antialiased',
        },
        h2: {
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 600,
          WebkitFontSmoothing: 'antialiased',
        },
        h3: {
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 600,
          WebkitFontSmoothing: 'antialiased',
        },
        h4: {
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 600,
          WebkitFontSmoothing: 'antialiased',
        },
        h5: {
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 600,
          WebkitFontSmoothing: 'antialiased',
          lineHeight: 1.334,
        },
        h6: {
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 600,
          WebkitFontSmoothing: 'antialiased',
        },
        subtitle1: {
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 600,
          WebkitFontSmoothing: 'antialiased',
        },
        subtitle2: {
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 600,
          WebkitFontSmoothing: 'antialiased',
        },
        body1: {
          fontFamily: 'Open Sans, Arial, sans-serif',
          fontWeight: 400,
          WebkitFontSmoothing: 'antialiased',
        },
        body2: {
          fontFamily: 'Open Sans, Arial, sans-serif',
          fontWeight: 400,
          WebkitFontSmoothing: 'antialiased',
        },
        caption: {
          fontFamily: 'Open Sans, Arial, sans-serif',
          fontWeight: 600,
          WebkitFontSmoothing: 'antialiased',
          fontSize: '0.75rem',
          lineHeight: 1.334,
          letterSpacing: '0.017em',
        },
        button: {
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 700,
          WebkitFontSmoothing: 'antialiased',
        },
      },
    },
    // Force Montserrat for buttons
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 600,
          lineHeight: 1.715,
        },
        sizeLarge: {
          padding: '16px 24px',
          fontSize: '1rem',
        },
      },
    },
  },
}

// Merge all theme configurations
export const theme = createTheme(
  cartoThemeOptions,
  customTheme(cartoThemeOptions as unknown as Theme),
  stepOverrides,
) as CustomTheme
